import { TextAreaWithLabel } from '@adotel/components'
import { DocumentNode, useMutation } from '@apollo/client'
import { Box, Button, CircularProgress, Typography } from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { Fragment, ReactNode, memo, useState } from 'react'
import { toast } from 'react-toastify'

import { ConfirmActionModal } from '../../components/ConfirmActionModal'
import { ListFilter } from '../../components/ListFilter'
import SectionTitle from '../../components/SectionTitle'
import * as notificationGraphql from '../../graphql/notifications'
import { useCursorPage } from '../../helpers/useCursorPage'
import NotificationResourceTable from './NotificationResourceTable'
import { ResourceColumn } from './resourceColumns'
import {
  RETAINED_RESULTS,
  ErrorState,
  LoadMoreButton,
  idempotencyKey,
  t,
  useFilteredRows
} from './shared'
import useStyles from './styles/ResourcePanel.styles'
import { ResourceRow } from './types'

type ResourcePanelProps = {
  title: string
  /**
   * Set false for a resource whose RPC does not accept a page size. Channel
   * health is the one: GetChannelHealth rejects any non-zero pageSize with
   * "channel health does not support pagination", and the pagination hook
   * injects one unconditionally, so every request failed.
   */
  paginated?: boolean
  /**
   * Set false where loading a row into the editor would lose a field.
   *
   * Provider configurations are the case: the list query returns
   * `credentialFingerprint` and never the credentials themselves, and the
   * mutation's `$credentials` is non-nullable, so a prefilled draft would
   * submit an empty credentials object for a configuration whose credentials
   * the operator never touched. Omitting the argument is not available — the
   * schema requires it.
   *
   * Withholding prefill keeps the previous behaviour there (a blank editor the
   * operator fills completely, credentials included) until Providers gets a
   * form that can express "leave credentials unchanged". Shipping prefill for
   * everything would have made a destructive save the low-friction path.
   */
  rowEditable?: boolean
  /** Which fields become columns. Declared at the call site because that is
   *  where the resource's shape is known — see resourceColumns.tsx. */
  columns: ResourceColumn[]
  /**
   * A real form for this resource, replacing the JSON textarea (§6).
   *
   * Supplied per resource rather than switched on inside, because only three
   * of the six have flat fields a form can express. Rules carry CEL source, so
   * their editor stays JSON — a form that lets an operator type CEL without
   * surfacing the compiler's errors would look like it validated and would not.
   */
  renderEditor?: (
    submit: (value: ResourceRow) => Promise<void>,
    saving: boolean,
    /**
     * The row being edited, or undefined when creating.
     *
     * Every form here already accepted an `initial` prop and nothing ever
     * passed one, so the editor was permanently blank: the only way to change a
     * record was to retype it in full, which is what "how do I edit this?"
     * meant. The rest of the portal opens a prefilled form from the row.
     */
    editing?: ResourceRow
  ) => ReactNode
  query: DocumentNode
  resultKey: string
  listKey: string
  canManage: boolean
  mutation?: DocumentNode
  variableName?: string
  deleteMutation?: DocumentNode
  actionMutation?: DocumentNode
  actionLabel?: string
  actionVariables?: (row: ResourceRow) => Record<string, unknown>
  mutationVariables?: (value: ResourceRow) => Record<string, unknown>
  queryVariables?: Record<string, unknown>
}

/**
 * What the operator must type to confirm a delete.
 *
 * The row's own identifier, because it is the one string already on screen for
 * every resource type this panel serves — these rows have no shared name field.
 * A generic word like "DELETE" would be typed reflexively; an id has to be read
 * off the record being deleted, which is the point of the signature.
 */
/**
 * A row's stable identity.
 *
 * NOT every resource has `id`. Categories are keyed by `key` —
 * NOTIFICATION_ADMIN_CATEGORIES_QUERY does not select `id` at all — while
 * templates, rules, subscriptions and provider configs do. Both call sites here
 * need the same answer, and keeping two fallback chains is how they drifted:
 * `deleteSignature` handled the `key` case and the editor's remount key did not,
 * so editing a Category showed a blank form under an "Editing …" heading.
 */
function rowIdentity(row: ResourceRow): string {
  return String(row.id ?? row.key ?? '')
}

function deleteSignature(row: ResourceRow): string {
  return rowIdentity(row)
}

const ResourceValue = memo(({ value }: { value: unknown }) => {
  const classes = useStyles()
  return (
    <pre className={classes.resourceValue}>
      {JSON.stringify(value, null, 2)}
    </pre>
  )
})

export function ResourcePanel({
  paginated = true,
  rowEditable = true,
  title,
  columns,
  renderEditor,
  query,
  resultKey,
  listKey,
  canManage,
  mutation,
  variableName,
  deleteMutation,
  actionMutation,
  actionLabel,
  actionVariables,
  mutationVariables,
  queryVariables = {}
}: ResourcePanelProps) {
  const classes = useStyles()
  // The shared cursor hook rather than a hand-rolled fetchMore. Its own
  // docstring is the reason: the `updateQuery` merge is a WORKAROUND for this
  // app building InMemoryCache with no typePolicies, so every paginated list
  // must merge by hand -- which makes a second copy dangerous rather than
  // merely repetitive. `appendBoundedResults` was that second copy, and
  // `[...current, ...next].slice(-limit)` is literally `maxRetained`.
  const {
    items: rows,
    loading,
    error,
    hasMore,
    loadMore,
    refetch
  } = useCursorPage<Record<string, any>, ResourceRow>({
    query,
    variables: queryVariables,
    pageSize: paginated ? undefined : null,
    maxRetained: RETAINED_RESULTS,
    getPage: (data) =>
      data && {
        items: data[resultKey]?.[listKey] ?? [],
        nextPageToken: data[resultKey]?.page?.nextPageToken
      },
    withItems: (data, items) => ({
      ...data,
      [resultKey]: { ...data[resultKey], [listKey]: items }
    })
  })
  const fallbackMutation = notificationGraphql.ARCHIVE_NOTIFICATIONS_MUTATION
  const [mutate, { loading: saving }] = useMutation(
    mutation ?? fallbackMutation
  )
  const [remove, { loading: deleting }] = useMutation(
    deleteMutation ?? fallbackMutation
  )
  const [act, { loading: acting }] = useMutation(
    actionMutation ?? fallbackMutation
  )
  const [draft, setDraft] = useState('{}')
  const [editing, setEditing] = useState<ResourceRow | undefined>()
  const [search, setSearch] = useState('')

  const filtering = Boolean(search.trim())
  const visibleRows = useFilteredRows(rows, search)

  // An empty table after filtering must not read as "none exist" — that sends
  // someone looking for missing data when the answer is to clear the filter.
  // It splits again on hasMore, which comes from the SERVER's cursor and is
  // unaffected by client-side filtering: more pages may hold matches, so
  // saying the search is finished would be wrong.
  const emptyMessage = filtering
    ? hasMore
      ? t(
          'No loaded {title} match this filter yet. Load more to keep looking.',
          {
            title: t(title).toLowerCase()
          }
        )
      : t('No loaded {title} match this filter.', {
          title: t(title).toLowerCase()
        })
    : undefined

  // Opens the editor on a row: the form is keyed by the row's identity so it
  // remounts with fresh state, and the JSON fallback is seeded with the row.
  // Without the key, React keeps the previous row's field values because the
  // component type has not changed.
  function editRow(row: ResourceRow): void {
    setEditing(row)
    setDraft(JSON.stringify(row, null, 2))
  }

  function stopEditing(): void {
    setEditing(undefined)
    setDraft('{}')
  }
  const [actionResult, setActionResult] = useState<unknown>()
  // The row awaiting confirmation. Holding the row rather than a boolean is what
  // lets the modal name the record being deleted and use its id as the typed
  // signature — §5 requires the signature to be readable on screen while the
  // user types it.
  const [pendingDelete, setPendingDelete] = useState<ResourceRow>()

  /**
   * One save path for both editors.
   *
   * The JSON textarea parses a string and hands over an object; a form hands
   * over the same object directly. Everything after that — variable shaping,
   * idempotency key, toast, refetch — is identical, and duplicating it is how
   * the two editors would drift.
   */
  async function submitValue(value: ResourceRow): Promise<void> {
    if (!variableName) return
    try {
      await mutate({
        variables: mutationVariables?.(value) ?? {
          [variableName]: value,
          idempotencyKey: idempotencyKey()
        }
      })
      toast.success(t('{title} saved.', { title: t(title) }))
      // Back to create mode: leaving the row loaded would make the next
      // save silently re-edit a record the operator has finished with.
      stopEditing()
      await refetch()
    } catch (mutationError) {
      toast.error(
        t('Unable to save {title}.', { title: t(title).toLowerCase() })
      )
    }
  }
  if (loading)
    return (
      <CircularProgress
        aria-label={t('Loading {title}', { title: t(title) })}
      />
    )
  if (error)
    return (
      <ErrorState
        message={t('Unable to load {title}.', {
          title: t(title).toLowerCase()
        })}
        onRetry={() => refetch()}
      />
    )
  return (
    <Box>
      {/* Names the CURRENT list, mirroring SupportTickets. The tab strip
          already carries the same word — Support duplicates it too, and that
          repetition is what anchors the page below a full-bleed strip. */}
      <SectionTitle title={t(title)} />

      <ListFilter
        label={title}
        value={search}
        onChange={setSearch}
        shown={visibleRows.length}
        loaded={rows.length}
      />

      <NotificationResourceTable
        title={title}
        rows={visibleRows}
        emptyMessage={emptyMessage}
        columns={columns}
        loading={loading}
        // Settled, not "not loading". The early returns above mean an error is
        // already excluded here, so an empty list at this point is genuinely
        // empty rather than not-yet-arrived.
        settled={!loading && !error}
        reloadData={() => refetch()}
        renderActions={(row) => (
          <>
            {rowEditable && canManage && mutation && variableName && (
              <Button
                onClick={() => editRow(row)}
                aria-label={t('Edit {title}', {
                  title: t(title).toLowerCase()
                })}>
                {t('Edit')}
              </Button>
            )}
            {canManage && actionMutation && (
              <Button
                disabled={acting}
                onClick={async () => {
                  try {
                    const result = await act({
                      variables: actionVariables?.(row) ?? {
                        id: row.id,
                        idempotencyKey: idempotencyKey()
                      }
                    })
                    setActionResult(result.data)
                    toast.success(
                      t('{action} completed.', { action: t(actionLabel ?? '') })
                    )
                    await refetch()
                  } catch (actionError) {
                    toast.error(
                      t('Unable to {action}.', {
                        action: t(actionLabel ?? '').toLowerCase()
                      })
                    )
                  }
                }}>
                {acting ? `${t(actionLabel ?? '')}…` : t(actionLabel ?? '')}
              </Button>
            )}
            {canManage && deleteMutation && (
              <Button
                color="secondary"
                disabled={deleting}
                onClick={() => setPendingDelete(row)}>
                {deleting ? t('Deleting…') : t('Delete')}
              </Button>
            )}
          </>
        )}
      />
      {actionResult && (
        <Box
          role="status"
          aria-label={t('Action result')}
          aria-live="polite"
          mt={2}>
          <Typography variant="subtitle1">{t('Action result')}</Typography>
          <ResourceValue value={actionResult} />
        </Box>
      )}
      {/* No ResultWindowControls: it existed to explain a home-grown 200-item
          window to the user. The shared hook bounds retention the same way, and
          every other portal list simply loads more without narrating it. */}
      {hasMore && <LoadMoreButton onLoadMore={async () => loadMore()} />}
      {canManage && mutation && variableName && renderEditor ? (
        <Box mt={2}>
          <div className={classes.editorHeader}>
            <Typography variant="subtitle1">
              {editing
                ? t('Editing {title}', { title: t(title).toLowerCase() })
                : t('Create {title}', { title: t(title).toLowerCase() })}
            </Typography>
            {editing && (
              <Button onClick={stopEditing}>{t('Cancel edit')}</Button>
            )}
          </div>
          {/* Keyed by the row's identity so the form remounts with that row's
              values. Without the key React reuses the mounted instance and
              keeps the previous row's state, so a second Edit would show the
              first row's fields. */}
          <Fragment key={editing ? rowIdentity(editing) : 'new'}>
            {renderEditor(submitValue, saving, editing)}
          </Fragment>
        </Box>
      ) : null}
      {canManage && mutation && variableName && !renderEditor && (
        <Box mt={2}>
          <div className={classes.editorHeader}>
            <Typography variant="subtitle1">
              {editing
                ? t('Editing {title}', { title: t(title).toLowerCase() })
                : t('Create {title}', { title: t(title).toLowerCase() })}
            </Typography>
            {editing && (
              <Button onClick={stopEditing}>{t('Cancel edit')}</Button>
            )}
          </div>
          <TextAreaWithLabel
            fullWidth
            label={t('{title} JSON', { title: t(title) })}
            noHelperText
            rows={8}
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={saving}
            className={classes.submitButton}
            onClick={async () => {
              try {
                const value: ResourceRow = JSON.parse(draft)
                await mutate({
                  variables: mutationVariables?.(value) ?? {
                    [variableName]: value,
                    idempotencyKey: idempotencyKey()
                  }
                })
                toast.success(t('{title} saved.', { title: t(title) }))
                await refetch()
              } catch (mutationError) {
                toast.error(
                  mutationError instanceof SyntaxError
                    ? t('Invalid JSON. Check the form and try again.')
                    : t('Unable to save {title}.', {
                        title: t(title).toLowerCase()
                      })
                )
              }
            }}>
            {saving ? t('Saving…') : t('Save')}
          </Button>
        </Box>
      )}
      {pendingDelete && (
        <ConfirmActionModal
          icon={<DeleteOutlineIcon />}
          // <bdi> because the identifier is a Latin token dropped into an
          // otherwise RTL dialog; without it the bidi algorithm reorders it.
          title={<bdi dir="ltr">{`${deleteSignature(pendingDelete)}`}</bdi>}
          // Names the consequence rather than asking "are you sure". These rows
          // are live notification configuration: deleting a rule stops facts
          // matching it, and nothing else on screen will show that it stopped.
          subtitle={t(
            'Deleting this stops it taking effect immediately. Notifications already sent are unaffected.'
          )}
          // The row's own identifier, which is visible in the row above — §5
          // requires the signature to be readable while the user types it.
          signature={deleteSignature(pendingDelete)}
          isOpen
          loading={deleting}
          onClose={() => setPendingDelete(undefined)}
          onClick={async () => {
            try {
              await remove({
                variables: {
                  id: pendingDelete.id,
                  idempotencyKey: idempotencyKey()
                }
              })
              toast.success(t('{title} item deleted.', { title: t(title) }))
              setPendingDelete(undefined)
              // Close the editor if it held the row just deleted. Leaving it
              // open offers Save on a record that no longer exists, and the
              // mutation is an upsert — so saving would silently recreate it.
              if (
                editing &&
                rowIdentity(editing) === rowIdentity(pendingDelete)
              ) {
                stopEditing()
              }
              await refetch()
            } catch (deleteError) {
              toast.error(
                t('Unable to delete {title} item.', {
                  title: t(title).toLowerCase()
                })
              )
            }
          }}
        />
      )}
    </Box>
  )
}
